import React, { useEffect, useState } from "react";
import API from "../utils/API";
import "./addTaskModal.css";

const PRIORITIES = ["Low", "Medium", "High", "Urgent"];

function formatDate(value) {
    return new Date(`${value}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/**
 * Modal for adding a Workspace task. The task can optionally be linked to a bill or a rulemaking item.
 *
 * teams:         the Workspaces the user can create tasks in: [{ team_id, team_name, can_assign, members: [{ user_id, name }] }]
 * defaultTeamId: the Workspace to start on (falls back to the first)
 * Reports the new task through onCreated(task) (a linked bill comes back as task.Bill) and closes through onClose().
 */
export default function AddTaskModal({ teams, defaultTeamId, userId, onCreated, onClose }) {
    const [teamId, setTeamId] = useState(teams.some(t => t.team_id === defaultTeamId) ? defaultTeamId : teams[0].team_id);
    const [text, setText] = useState("");
    const [assignee, setAssignee] = useState("");
    const [due, setDue] = useState("");
    const [priority, setPriority] = useState("");
    const [linkType, setLinkType] = useState("none");
    const [bill, setBill] = useState(null);
    const [rulemaking, setRulemaking] = useState(null);
    const [search, setSearch] = useState("");
    const [billResults, setBillResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [trackedBills, setTrackedBills] = useState([]);
    const [rulemakingItems, setRulemakingItems] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const team = teams.find(t => t.team_id === teamId);
    const assignable = team.can_assign ? team.members : team.members.filter(m => m.user_id === userId);

    // The Workspace's tracked bills, offered before the user types anything
    useEffect(() => {
        let cancelled = false;
        setTrackedBills([]);
        API.getWorkspaceTrackedBills(teamId)
            .then(res => { if (!cancelled) setTrackedBills(res.data); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [teamId]);

    // Upcoming rulemaking items, loaded the first time that link type is chosen
    useEffect(() => {
        if (linkType !== "rulemaking" || rulemakingItems) return;
        API.getDashboardRulemaking()
            .then(res => setRulemakingItems(res.data))
            .catch(() => setRulemakingItems([]));
    }, [linkType, rulemakingItems]);

    useEffect(() => {
        if (linkType !== "bill" || search.trim().length < 2) {
            setBillResults([]);
            return;
        }
        const timer = setTimeout(() => {
            setSearching(true);
            API.searchBills(search.trim())
                .then(res => setBillResults((Array.isArray(res.data) ? res.data : (res.data.rows || [])).slice(0, 10)))
                .catch(() => setBillResults([]))
                .finally(() => setSearching(false));
        }, 300);
        return () => clearTimeout(timer);
    }, [search, linkType]);

    const changeTeam = id => {
        setTeamId(id);
        setAssignee("");
    };

    const changeLinkType = type => {
        setLinkType(type);
        setSearch("");
    };

    const pickBill = b => {
        setBill(b);
        setSearch("");
    };

    const pickRulemaking = item => {
        setRulemaking(item);
        setSearch("");
        if (!due) setDue(item.date);
    };

    const addTask = async () => {
        if (!text.trim() || saving) return;
        setSaving(true);
        setError("");
        const fields = {
            task_text: text.trim(),
            assigned_to_user_id: assignee || null,
            due_date: due || null,
            priority: priority || null,
            created_by_user_id: userId,
        };
        try {
            let task;
            if (linkType === "bill" && bill) {
                const res = await API.createBillTask(team.team_id, bill.bill_id, fields);
                task = { ...res.data, Bill: { bill_id: bill.bill_id, bill_type: bill.bill_type, bill_number: bill.bill_number, title: bill.title } };
            } else if (linkType === "rulemaking" && rulemaking) {
                const res = await API.createRulemakingTask(team.team_id, {
                    ...fields,
                    rulemaking_ref: rulemaking.ref,
                    rulemaking_title: `${rulemaking.agency}: ${rulemaking.subject}`,
                    rulemaking_url: rulemaking.source_url || null,
                });
                task = res.data;
            } else {
                task = (await API.createTeamTask(team.team_id, fields)).data;
            }
            onCreated(task);
            onClose();
        } catch (err) {
            setError(err.response?.data?.message || "Could not add the task.");
            setSaving(false);
        }
    };

    const query = search.trim().toLowerCase();
    const billOptions = query.length >= 2
        ? billResults
        : trackedBills.slice(0, 8);
    const rulemakingOptions = (rulemakingItems || [])
        .filter(item => !query || `${item.agency} ${item.subject}`.toLowerCase().includes(query))
        .slice(0, 8);
    const selected = linkType === "bill" ? bill : linkType === "rulemaking" ? rulemaking : null;

    return (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.45)" }} onClick={onClose}>
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable atm-dialog" onClick={e => e.stopPropagation()}>
                <div className="modal-content">
                    <div className="modal-header atm-header">
                        <h5 className="modal-title atm-title">Add Task</h5>
                    </div>

                    <div className="modal-body atm-body">
                        {teams.length > 1 && (
                            <div className="atm-field">
                                <label className="atm-label" htmlFor="atm-team">Workspace</label>
                                <select id="atm-team" className="form-select form-select-sm" value={team.team_id} onChange={e => changeTeam(Number(e.target.value))}>
                                    {teams.map(t => <option key={t.team_id} value={t.team_id}>{t.team_name}</option>)}
                                </select>
                            </div>
                        )}

                        <div className="atm-field">
                            <label className="atm-label" htmlFor="atm-text">Task</label>
                            <textarea
                                id="atm-text"
                                className="form-control form-control-sm"
                                rows={3}
                                value={text}
                                autoFocus
                                maxLength={500}
                                placeholder="e.g. Schedule a call with the coalition"
                                onChange={e => setText(e.target.value)}
                            />
                        </div>

                        <div className="atm-field">
                            <label className="atm-label">Link to <span className="atm-optional">(optional)</span></label>
                            <div className="atm-toggle">
                                <button type="button" className={`atm-toggle-btn${linkType === "none" ? " active" : ""}`} onClick={() => changeLinkType("none")}>None</button>
                                <button type="button" className={`atm-toggle-btn${linkType === "bill" ? " active" : ""}`} onClick={() => changeLinkType("bill")}>Legislation</button>
                                <button type="button" className={`atm-toggle-btn${linkType === "rulemaking" ? " active" : ""}`} onClick={() => changeLinkType("rulemaking")}>Rulemaking</button>
                            </div>

                            {selected && (
                                <div className="atm-selected">
                                    {linkType === "bill" ? (
                                        <>
                                            <span className="atm-selected-num">{bill.bill_type} {bill.bill_number}</span>
                                            <span className="atm-selected-title">{bill.title}</span>
                                        </>
                                    ) : (
                                        <>
                                            <span className="atm-selected-num">{rulemaking.agency}</span>
                                            <span className="atm-selected-title">{rulemaking.subject}</span>
                                        </>
                                    )}
                                    <button type="button" className="atm-selected-clear" title="Remove link" onClick={() => (linkType === "bill" ? setBill(null) : setRulemaking(null))}>
                                        <i className="bi bi-x-lg" />
                                    </button>
                                </div>
                            )}

                            {linkType !== "none" && !selected && (
                                <div className="atm-picker">
                                    <input
                                        className="form-control form-control-sm"
                                        placeholder={linkType === "bill" ? "Search by bill number or keyword…" : "Filter upcoming hearings and comment deadlines…"}
                                        value={search}
                                        onChange={e => setSearch(e.target.value)}
                                    />
                                    <div className="atm-options">
                                        {linkType === "bill" && (
                                            <>
                                                {query.length < 2 && billOptions.length > 0 && <div className="atm-options-label">Tracked by this Workspace</div>}
                                                {searching && <div className="atm-option atm-option--muted">Searching…</div>}
                                                {!searching && billOptions.length === 0 && (
                                                    <div className="atm-option atm-option--muted">{query.length < 2 ? "Type to search for a bill." : "No bills found."}</div>
                                                )}
                                                {billOptions.map(b => (
                                                    <div key={b.bill_id} className="atm-option" onClick={() => pickBill(b)}>
                                                        <span className="atm-option-main">{b.bill_type} {b.bill_number}</span>
                                                        <span className="atm-option-sub">{b.title}</span>
                                                    </div>
                                                ))}
                                            </>
                                        )}
                                        {linkType === "rulemaking" && (
                                            <>
                                                {!rulemakingItems && <div className="atm-option atm-option--muted">Loading…</div>}
                                                {rulemakingItems && rulemakingOptions.length === 0 && <div className="atm-option atm-option--muted">No upcoming rulemaking items found.</div>}
                                                {rulemakingOptions.map(item => (
                                                    <div key={item.id} className="atm-option" onClick={() => pickRulemaking(item)}>
                                                        <span className="atm-option-main">{item.agency}</span>
                                                        <span className="atm-option-sub">
                                                            {item.kind === "hearing" ? "Hearing" : "Comments due"} {formatDate(item.date)} · {item.subject}
                                                        </span>
                                                    </div>
                                                ))}
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="atm-field">
                            <label className="atm-label" htmlFor="atm-assignee">Assign to</label>
                            <select id="atm-assignee" className="form-select form-select-sm" value={assignee} onChange={e => setAssignee(e.target.value)}>
                                <option value="">Unassigned</option>
                                {assignable.map(m => <option key={m.user_id} value={m.user_id}>{m.user_id === userId ? `${m.name} (me)` : m.name}</option>)}
                            </select>
                        </div>

                        <div className="atm-row">
                            <div className="atm-field">
                                <label className="atm-label" htmlFor="atm-due">Due date</label>
                                <input id="atm-due" type="date" className="form-control form-control-sm" value={due} onChange={e => setDue(e.target.value)} />
                            </div>
                            <div className="atm-field">
                                <label className="atm-label" htmlFor="atm-priority">Priority</label>
                                <select id="atm-priority" className="form-select form-select-sm" value={priority} onChange={e => setPriority(e.target.value)}>
                                    <option value="">None</option>
                                    {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                                </select>
                            </div>
                        </div>

                        {error && <div className="atm-error">{error}</div>}
                    </div>

                    <div className="modal-footer atm-footer">
                        <button type="button" className="btn atm-cancel-btn" onClick={onClose}>Cancel</button>
                        <button type="button" className="btn atm-save-btn" disabled={!text.trim() || saving} onClick={addTask}>
                            {saving ? "Adding…" : "Add Task"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
